import * as React from 'react'
import { Button } from 'primereact/button'
import { InputProps } from './InputTypes'
import { createBrowserHistory } from 'history'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * 보안 키패드 입력
 */
export type InputSecureFieldReturn = {
    /**
     * transkey를 적용한 field ID
     */
    id: string
    /**
     * 암호화된 데이터 값
     */
    transkeyPwd: string
    /**
     * hmac 데이터
     */
    transkeyHM: string
    /**
     * Field Keyboard type 값
     */
    keyboardType: string
    /**
     * keyIndex 값
     */
    keyIndex: string
    /**
     * 보안필드 타입
     */
    fieldType: string
    /**
     * 암호화된 seedKey 값
     */
    seedKey: string
    /**
     * 암호화 된 초기화 시간 값
     */
    initTime: string
    /**
     * 주민번호 앞 번호등 기타 전달이 필요한 항목
     */
    extras: string
}
export type InputSecureFieldProps = Omit<InputProps, 'onChange' | 'onClear'> & {
    /**
     * 고유 id값(필수, 중복 시 동작 불가)
     */
    id: string
    /**
     * 보안키패드에 초기 설정할 값
     */
    value?: string
    /**
     * 최대 입력 값
     */
    maxLength: number
    /**
     * 표시할 키패드 타입
     */
    type?: 'number' | 'qwerty'
    /**
     * 값이 변경되면 trigger,
     * TODO: 필요한지 의문?
     * @param value - 입력된 값(평문)
     */
    //
    onChange?(value: string): void
    /**
     * 보안 키패드가 닫힐때 trigger
     * @param value - 입력된 값(평문)
     * @param isFulfilled - 입력이 완료되었는지 여부(입력값 >= maxLength)
     */
    onClose?(value: string, isFulfilled: boolean): void
    /**
     * label class
     */
    labelClassName?: string
}

export type InputSecureFieldRef = {
    /**
     * 입력된 값을 받는다.(평문)
     * @deprecated 제거될 수 있음... 사용하는 경우 문의
     */
    getData(): string
    /**
     * 입력값을 암호화 한 후 받는다. 서버 전송시 사용
     */
    getSecureData(): InputSecureFieldReturn
    /**
     * 입력이 완료되었는지를 반환한다.
     */
    isFulfilled(): boolean
    /**
     * 보안키패드를 표시한다.(버튼을 누르는데, ref가 버튼이 아니므로)
     */
    focus(): void
    /**
     * 보안키패드를 값을 초기화 한다.
     */
    clear(): void
}

export const InputSecureField = React.forwardRef<
    InputSecureFieldRef,
    InputSecureFieldProps
>((props, ref) => {
    const {
        value = '',
        id,
        label = '',
        title = '',
        maxLength,
        type = 'number',
        onChange,
        onClose,
        //--InputField와 동일한 구조가 필요해서 추가
        className = '',
        labelClassName = 'mt-32',
        result = 'hidden',
        resultId = '',
        resultMsg,
        disabled = false,
    } = props

    //입력용 Dummy Input
    const secureInputRef = React.useRef<HTMLInputElement>(null)
    //입력값
    const [secureValue, setSecureValue] = React.useState('')
    //입력값 Ref
    const secureValueRef = React.useRef(secureValue)
    //포커스를 위한... 인터페이스가 열려있지 않다.
    const buttonRef = React.useRef(null)
    //현재 입력 길이(*** 표시를 위해)
    const [count, setCount] = React.useState(value.length)
    //핸들러 업데이트를 위한 단순 State
    const [updateHandler, setUpdateHandler] = React.useState(0)

    React.useImperativeHandle(
        ref,
        () => ({
            getSecureData: getSecureData,
            //제거될 수 있음... 사용하는 경우 문의
            getData: () => secureValueRef.current,
            isFulfilled: () => {
                return secureValueRef.current.length >= maxLength
            },
            focus: handleFocus,
            clear: () => {
                setCount(0)
                setSecureValue('')
                secureValueRef.current = ''
                secureInputRef.current.value = ''
            },
        }),
        [secureValueRef.current]
    )

    React.useEffect(() => {
        //(필수)native input field가 생성된 이후 호출해야함.
        globalThis.initmTranskey()
    }, [])

    React.useEffect(() => {
        //이벤트가 한번에 한개만 등록 가능해서 수정함.
        const mtk = globalThis.mtk
        if (!mtk) return
        mtk.startCallBack = mtk.clearCallBack = mtk.delCallBack = keypadCallback
        mtk.doneCallBack = mtk.closeCallBack = handleClose
        return () => {
            mtk.startCallBack = mtk.clearCallBack = mtk.delCallBack = null
            mtk.doneCallBack = mtk.closeCallBack = null
        }
    }, [updateHandler])

    React.useEffect(() => {
        //초기 값을 외부에서 변경해야해서 수정
        if (secureInputRef.current) {
            secureInputRef.current.value = value
            secureValueRef.current = value
            setSecureValue(value)
            setCount(value.length)
        }
    }, [value])

    const handleFocus = () => {
        //외부에서 포커스를 줄수 있어야해서 수정
        globalThis.mtk.onKeyboard(secureInputRef.current)
        /**
         * 키패드 타이틀 관련 기능 미리 준비
         */
        // globalThis.mtk.now.div.firstChild.firstChild.innerText = label
        setCount(0)
        setSecureValue('')
        setUpdateHandler((prev) => prev + 1)
        buttonRef.current?.focus()
    }

    const keypadCallback = () => {
        if (!globalThis.mtk.now) return
        if (globalThis.mtk.now.inputObj !== secureInputRef.current) return
        updateKeypadValue()
    }

    const updateKeypadValue = () => {
        const aValue = secureInputRef.current.value ?? ''
        secureValueRef.current = aValue
        setCount(aValue.length)
        setSecureValue(aValue)
        onChange?.(aValue)
    }

    const handleClose = () => {
        updateKeypadValue()
        onClose?.(
            secureValueRef.current,
            secureValueRef.current.length >= maxLength
        )
    }

    const getSecureData = (): InputSecureFieldReturn => {
        globalThis.mtk.fillEncData()

        return {
            id: id,
            transkeyPwd: (
                document.getElementById(`transkey_${id}`) as HTMLInputElement
            ).value,
            transkeyHM: (
                document.getElementById(`transkey_HM_${id}`) as HTMLInputElement
            ).value,
            keyboardType: (
                document.getElementById(
                    `keyboardType_${id}`
                ) as HTMLInputElement
            ).value,
            keyIndex: (
                document.getElementById(`keyIndex_${id}`) as HTMLInputElement
            ).value,
            fieldType: (
                document.getElementById(`fieldType_${id}`) as HTMLInputElement
            ).value,
            seedKey: (document.getElementById('seedKey') as HTMLInputElement)
                .value,
            initTime: (document.getElementById('initTime') as HTMLInputElement)
                .value,
            //제거될 수 있음... 사용하는 경우 문의
            extras: secureValueRef.current,
        }
    }

    const resultCheck = () => {
        let rt = null
        if (result !== 'hidden' && resultMsg) {
            rt = (
                <p id={resultId} className={`p-msg-${result}`}>
                    {resultMsg}
                </p>
            )
        }
        return rt
    }

    return (
        <>
            {label.length > 0 && (
                <span className={`inputs-label ${labelClassName}`.trim()}>
                    {label}
                </span>
            )}
            <div
                className={`input-basic ${
                    disabled ? '' : '-force-active'
                } ${className} ${result === 'error' ? '-error' : ''}`.trim()}
            >
                <div className="input">
                    <input
                        type="password"
                        id={id}
                        name={id}
                        maxLength={maxLength}
                        // value={secureValue}
                        ref={secureInputRef}
                        onChange={(e) => {
                            //단지 lint error로 추가
                        }}
                        data-tk-nextsibling="false"
                        data-tk-kbdtype={type}
                        data-tk-bottom="true"
                        style={{
                            display: 'none',
                        }}
                        {...{ disabled }}
                    />
                    <Button
                        pt={{
                            root: {
                                ref: buttonRef,
                            },
                        }}
                        className="password"
                        onClick={handleFocus}
                        {...{ disabled }}
                    >
                        <div className={`keypad-password-${maxLength}`}>
                            <div
                                title={title}
                                className={`input-btn-keypad -digit${count}`}
                            ></div>
                        </div>
                    </Button>
                </div>
            </div>
            {resultCheck()}
        </>
    )
})
